import { fetchProjectChats, useSDK } from '@littlebird/sdk';
import { useQueries } from '@tanstack/react-query';

import { type TeamChatRow } from '@/components/blocks/Layout/Sidebar/ChatSection.items';
import { userStore } from '@/stores/user.store';

import { useTeamChatsEnabled } from './use-team-chat-config';

const NO_ROWS: ReadonlyMap<string, readonly TeamChatRow[]> = new Map();

export function useTeamChatRows(
  projectIds: ReadonlySet<string>
): ReadonlyMap<string, readonly TeamChatRow[]> {
  const sdk = useSDK();
  const viewerId = Number(userStore.userId);
  const ids = useTeamChatsEnabled() ? [...projectIds] : [];
  return useQueries({
    queries: ids.map((projectId) => ({
      // Under the project's chats key, so the same invalidation refreshes it
      queryKey: ['project-chats', projectId, 'teammates'] as const,
      queryFn: () => fetchProjectChats(sdk, projectId),
      select: (page: Awaited<ReturnType<typeof fetchProjectChats>>) =>
        page.items
          .filter((item) => item.authorUserId !== viewerId)
          .map((item): TeamChatRow => ({
            id: item.id,
            title: item.title,
            posters: item.posters ?? [],
            unread: item.unread ?? false,
          })),
    })),
    combine: (results) =>
      ids.length === 0
        ? NO_ROWS
        : new Map(
            ids.map((projectId, index) => [
              projectId,
              results[index]?.data ?? [],
            ])
          ),
  });
}
